// The Samen tab's count and the home-screen icon's badge: open questions plus unread updates
// (GET /v1/inbox/summary). Outside a tasting nothing polls: the count is read at start, when the
// app comes back into view or online, when a social push arrives (the service worker says so), and
// after the inbox itself changes it.
import { useSyncExternalStore } from "react";
import { isAppRoute } from "../pushNotification";
import { getAuthState, onSessionChange } from "./auth";
import { getInboxSummary } from "./inboxApi";
import { clearSocialOutbox, flushSocialOutbox } from "./outbox";

/** What the service worker posts to open windows when a social push arrives. */
export const INBOX_CHANGED_MESSAGE = "proef:inbox";
/** What it posts when a notification is tapped while a window is already open. */
export const OPEN_ROUTE_MESSAGE = "proef:open";

let count = 0;
const listeners = new Set<() => void>();

type BadgingNavigator = Navigator & {
  setAppBadge?: (contents?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
};

/** The Badging API where the platform has it (installed PWAs on most desktops, iOS 16.4+). */
function applyAppBadge(next: number): void {
  const nav: BadgingNavigator = navigator;
  const done = next > 0 ? nav.setAppBadge?.(next) : nav.clearAppBadge?.();
  // A refused badge (no permission, not installed) only loses the icon dot.
  done?.catch(() => undefined);
}

export function setInboxBadge(next: number): void {
  if (next === count) return;
  count = next;
  applyAppBadge(next);
  listeners.forEach((listener) => {
    listener();
  });
}

export function getInboxBadge(): number {
  return count;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useInboxBadge(): number {
  return useSyncExternalStore(subscribe, getInboxBadge, () => 0);
}

let refreshing: Promise<void> | undefined;

/** Re-reads the count; signed out it is zero. A failed read keeps the last count. */
export function refreshInboxBadge(): Promise<void> {
  refreshing ??= (async () => {
    try {
      const auth = getAuthState();
      if (auth.status !== "signedIn") {
        if (auth.status === "signedOut") setInboxBadge(0);
        return;
      }
      await flushSocialOutbox(auth.user.id);
      setInboxBadge((await getInboxSummary()).badge);
    } catch {
      // Offline or the edge away: the next natural moment tries again.
    } finally {
      refreshing = undefined;
    }
  })();
  return refreshing;
}

function isMessage(data: unknown, type: string): boolean {
  return typeof data === "object" && data !== null && Reflect.get(data, "type") === type;
}

/** Starts the count's triggers; returns the stop. The tab bar runs it for as long as it is shown. */
export function startInboxBadgeWatch(): () => void {
  let lastUser: string | undefined;
  const onSession = (): void => {
    const auth = getAuthState();
    const user = auth.status === "signedIn" ? auth.user.id : undefined;
    if (auth.status === "signedOut" && lastUser !== undefined) clearSocialOutbox();
    if (user !== lastUser || auth.status === "signedOut") void refreshInboxBadge();
    if (auth.status !== "loading" && auth.status !== "error") lastUser = user;
  };
  const onVisible = (): void => {
    if (document.visibilityState === "visible") void refreshInboxBadge();
  };
  const onRefresh = (): void => {
    void refreshInboxBadge();
  };
  const onMessage = (event: MessageEvent): void => {
    if (isMessage(event.data, INBOX_CHANGED_MESSAGE)) void refreshInboxBadge();
  };
  const stopSession = onSessionChange(onSession);
  window.addEventListener("online", onRefresh);
  document.addEventListener("visibilitychange", onVisible);
  const worker = "serviceWorker" in navigator ? navigator.serviceWorker : undefined;
  worker?.addEventListener("message", onMessage);
  onSession();
  return () => {
    stopSession();
    window.removeEventListener("online", onRefresh);
    document.removeEventListener("visibilitychange", onVisible);
    worker?.removeEventListener("message", onMessage);
  };
}

/** Moves an open window to the item a tapped notification names; returns the stop. */
export function startNotificationRouteWatch(open: (route: string) => void): () => void {
  if (!("serviceWorker" in navigator)) return () => undefined;
  const worker = navigator.serviceWorker;
  const onMessage = (event: MessageEvent): void => {
    const data: unknown = event.data;
    if (typeof data !== "object" || data === null || !isMessage(data, OPEN_ROUTE_MESSAGE)) return;
    const route: unknown = Reflect.get(data, "route");
    if (isAppRoute(route)) open(route);
  };
  worker.addEventListener("message", onMessage);
  return () => {
    worker.removeEventListener("message", onMessage);
  };
}

/** Tests start from zero. */
export function resetInboxBadge(): void {
  count = 0;
  refreshing = undefined;
}
